import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Users,
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  UserPlus,
  ArrowRight,
  TrendingUp,
  XCircle,
  Eye,
} from 'lucide-react';
import { intakeService } from '@/services/api/intake';
import { cn } from '@/lib/utils';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';

const RISK_COLORS = {
  low: '#22c55e',
  medium: '#eab308',
  high: '#f97316',
  critical: '#ef4444',
};

const STATUS_COLORS = {
  pending: '#6b7280',
  active: '#3b82f6',
  submitted: '#8b5cf6',
  under_review: '#f59e0b',
  approved: '#22c55e',
  rejected: '#ef4444',
};

export function IntakeDashboardPage() {
  const navigate = useNavigate();

  // Fetch dashboard stats
  const { data: stats, isLoading } = useQuery({
    queryKey: ['intake-dashboard-stats'],
    queryFn: () => intakeService.getDashboardStats(),
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch recent clients
  const { data: clientsData } = useQuery({
    queryKey: ['intake-clients', { pageSize: 5 }],
    queryFn: () => intakeService.listClients({ pageSize: 5, sortBy: 'createdAt', sortOrder: 'desc' }),
  });

  const recentClients = clientsData?.clients || [];

  // Prepare chart data
  const statusChartData = stats ? [
    { name: 'Pending', value: stats.pendingClients, color: STATUS_COLORS.pending },
    { name: 'Active', value: stats.activeClients, color: STATUS_COLORS.active },
    { name: 'Submitted', value: stats.submittedClients, color: STATUS_COLORS.submitted },
    { name: 'Under Review', value: stats.underReviewClients, color: STATUS_COLORS.under_review },
    { name: 'Approved', value: stats.approvedClients, color: STATUS_COLORS.approved },
    { name: 'Rejected', value: stats.rejectedClients, color: STATUS_COLORS.rejected },
  ].filter(d => d.value > 0) : [];

  const riskChartData = stats ? [
    { name: 'Low', value: stats.riskDistribution.low, color: RISK_COLORS.low },
    { name: 'Medium', value: stats.riskDistribution.medium, color: RISK_COLORS.medium },
    { name: 'High', value: stats.riskDistribution.high, color: RISK_COLORS.high },
    { name: 'Critical', value: stats.riskDistribution.critical, color: RISK_COLORS.critical },
  ].filter(d => d.value > 0) : [];

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; className: string }> = {
      pending: { label: 'Pending', className: 'bg-gray-500/10 text-gray-500' },
      active: { label: 'Active', className: 'bg-blue-500/10 text-blue-500' },
      submitted: { label: 'Submitted', className: 'bg-purple-500/10 text-purple-500' },
      under_review: { label: 'Under Review', className: 'bg-amber-500/10 text-amber-500' },
      approved: { label: 'Approved', className: 'bg-green-500/10 text-green-500' },
      rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-500' },
    };
    const { label, className } = config[status] || config.pending;
    return <Badge className={className}>{label}</Badge>;
  };

  const getRiskBadge = (level?: string) => {
    if (!level) return null;
    const config: Record<string, { label: string; className: string }> = {
      low: { label: 'Low Risk', className: 'bg-green-500/10 text-green-500' },
      medium: { label: 'Medium Risk', className: 'bg-yellow-500/10 text-yellow-500' },
      high: { label: 'High Risk', className: 'bg-orange-500/10 text-orange-500' },
      critical: { label: 'Critical', className: 'bg-red-500/10 text-red-500' },
    };
    const { label, className } = config[level] || config.low;
    return <Badge className={className}>{label}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Intake Dashboard</h1>
          <p className="text-muted-foreground">
            Overview of client intake progress and document management
          </p>
        </div>
        <Button onClick={() => navigate('/panel/intake/clients/new')}>
          <UserPlus className="mr-2 h-4 w-4" />
          Add Client
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Clients</p>
                {isLoading ? (
                  <Skeleton className="h-8 w-16 mt-1" />
                ) : (
                  <p className="text-3xl font-bold">{stats?.totalClients || 0}</p>
                )}
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/10">
                <Users className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                {isLoading ? (
                  <Skeleton className="h-8 w-16 mt-1" />
                ) : (
                  <p className="text-3xl font-bold">{stats?.underReviewClients || 0}</p>
                )}
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10">
                <Clock className="h-6 w-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                {isLoading ? (
                  <Skeleton className="h-8 w-16 mt-1" />
                ) : (
                  <p className="text-3xl font-bold">{stats?.approvedClients || 0}</p>
                )}
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10">
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">High Risk</p>
                {isLoading ? (
                  <Skeleton className="h-8 w-16 mt-1" />
                ) : (
                  <p className="text-3xl font-bold">
                    {(stats?.riskDistribution.high || 0) + (stats?.riskDistribution.critical || 0)}
                  </p>
                )}
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-500/10">
                <AlertTriangle className="h-6 w-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Client Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Client Status Distribution</CardTitle>
            <CardDescription>Breakdown of clients by intake status</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : statusChartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      cx="35%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip  cursor={false}/>
                    <Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: '11px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Risk Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Risk Assessment Distribution</CardTitle>
            <CardDescription>Clients categorized by risk level</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : riskChartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={riskChartData} layout="vertical">
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={80} />
                    <Tooltip  cursor={false}/>
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} activeBar={false}>
                      {riskChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Document Stats & Recent Clients */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Document Processing Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Document Processing</CardTitle>
            <CardDescription>Overview of document upload and validation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-blue-500" />
                    <span className="text-sm">Total Documents</span>
                  </div>
                  <span className="font-semibold">{stats?.totalDocuments || 0}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-amber-500" />
                    <span className="text-sm">Pending Review</span>
                  </div>
                  <span className="font-semibold">{stats?.pendingDocuments || 0}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span className="text-sm">Approved</span>
                  </div>
                  <span className="font-semibold">{stats?.approvedDocuments || 0}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <XCircle className="h-5 w-5 text-red-500" />
                    <span className="text-sm">Rejected</span>
                  </div>
                  <span className="font-semibold">{stats?.rejectedDocuments || 0}</span>
                </div>

                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Avg. Completion Rate</span>
                    <span className="font-semibold">{stats?.avgCompletionRate || 0}%</span>
                  </div>
                  <Progress value={stats?.avgCompletionRate || 0} className="h-2" />
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Avg. Processing Time</span>
                  </div>
                  <span className="font-semibold">{stats?.avgProcessingTime || 0}h</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Recent Clients */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Clients</CardTitle>
              <CardDescription>Latest client intake submissions</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/panel/intake/clients')}>
              View All
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-14 w-full" />
                ))}
              </div>
            ) : recentClients.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No clients yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentClients.map((client) => (
                  <div
                    key={client.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/panel/intake/clients/${client.id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{client.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{client.company || client.email}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {getRiskBadge(client.riskLevel)}
                      {getStatusBadge(client.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => navigate('/panel/intake/clients')}>
              <Users className="mr-2 h-4 w-4" />
              Manage Clients
            </Button>
            <Button variant="outline" onClick={() => navigate('/panel/intake/document-types')}>
              <FileText className="mr-2 h-4 w-4" />
              Document Types
            </Button>
            <Button variant="outline" onClick={() => navigate('/panel/intake/clients?status=under_review')}>
              <Eye className="mr-2 h-4 w-4" />
              Review Queue ({stats?.underReviewClients || 0})
            </Button>
            <Button variant="outline" onClick={() => navigate('/panel/intake/faqs')}>
              <FileText className="mr-2 h-4 w-4" />
              Manage FAQs
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
